import { useCallback, useEffect, useState } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";
import { useNavigate } from "react-router-dom";
import { Box, Divider, Stack } from "@mui/material";

import ItemInput from "../../components/Input/ItemInput";

import {
  ConfirmActionModal,
  ActionModal,
} from "../../components/Modals/ActionModal";
import { ExpandableContainer } from "../../components/Collapsible/ExpandableContainer";
import DamsTable from "../../components/Table/CollapsibleTable";
import { TRowItem } from "../../components/Table/TableRow";
import { ContainedButton } from "../../components/Button/AppButton";
import { DamsAppAsset } from "../../assets/registry";
import VerticalGap from "../../components/Gap/VerticalGap";
import { assetApi } from "../../services/assets/asset-crud";
import { DatePicker } from "../../components/calender";
import { FormSchemaType, useDmsForm } from "../../hooks/useDmsFrom";
import {
  IAssetMaintenanceSchedule,
  IAssetRecord,
  IPagination,
  IQueryPerams,
  PageActions,
} from "../../config/types";
import { usePageModels } from "../../hooks/usePageActions";
import dayjs from "dayjs";
import { AssetMaintenanceApi } from "../../services/maintenance";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { usePagination } from "../../hooks/usePagination";
import appRoutePath from "../../config/path";
import ItemSelectInput from "../../components/Input/ItemSelectInput";
import { ASSET_SCHEDULE_STATUS } from "../../config/constants/common";
import { formatDate } from "../../utils/common";
import { AssetLogsFilter } from "../../common-ui/page-filters";
import LocalStorageService from "../../store/localStorage";
import { alertDialogStore } from "../../store/dialogs";
const INITAIL_VALUE: IAssetMaintenanceSchedule = {
  service_date: "",
  service_remarks: "",
  schedule_status: "",
  assets: [],
};

function AssetMaintenanceSchedule() {
  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);
  const {
    form,
    updateForm,
    updateInputField,
    fieldHasError,
    setSubmitAttempt,
    isFormValid,
    getFormValues,
    resetForm,
  } = useDmsForm<IAssetMaintenanceSchedule>(INITAIL_VALUE);
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(assetApi.getAssets);
  const [pageAction, updatePageAction] = useState<PageActions>();
  const { models, openModel, closeModel } = usePageModels();
  const [isProcessing, setProcess] = useState<boolean>(false);
  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });

  const handleDatePicker = (date: string) => {
    updateForm((p) => ({
      ...p,
      service_date: { value: date, isValid: true },
    }));
  };

  useEffect(() => {
    updateForm((prev) => ({
      ...prev,
      assets: {
        value: selectedRowItems.map((row) => row.id),
        isValid: selectedRowItems.length > 0,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowItems.length]);

  const fetchData = useCallback(async () => {
    const result = await assetApi.getAssets({ is_deleted: false });
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const populateDataSet = (data: IAssetRecord[], pagination?: IPagination) => {
    const formatData = data.map((data) => ({
      ...data,
      brand: `${data.brand}(${data.model})`,
      date_received: formatDate(data.date_received),
      warranty_in_months: data.warranty_in_months ?? "Not Stated",
      asset_meta_raw: data.asset_meta
        ? data.asset_meta.map(
            (item) => ` ${item.asset_tag}=${item.meta_value} `,
          )
        : [],
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const prevFormState =
      LocalStorageService.get<FormSchemaType<IAssetMaintenanceSchedule>>(
        "MAINTENANCE_FORM",
      );
    if (prevFormState) {
      updateForm(prevFormState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cancelAddItem() {
    setUnsavedChanges(true);
  }
  function saveChanges() {
    LocalStorageService.save("MAINTENANCE_FORM", form);
    setUnsavedChanges(false);
    alertDialogStore.open({
      title: "Save your changes",
      subtitle: "current changes saved on device",
      onLeftBtnPress: () => {},
      onRightBtnPress: onSeeAllPress,
      isSuccess: false,
      rightBtnTxt: "Continue",
      leftBtnTxt: "Close",
    });
  }

  async function onSaveItemClick() {
    updatePageAction({
      title: "Schedule Asset for mantainance",
      type: "add",
      message: `You are scheduling  ${selectedRowItems.length} asset for maintainance. Click "Confirm" to continue.`,
    });
    openModel("add");
  }

  async function confirmUserSave() {
    setSubmitAttempt(true);
    if (isFormValid()) {
      setProcess(true);
      const data = getFormValues();
      const result =
        await AssetMaintenanceApi.createAssetMaintenanceSchedule(data);
      setProcess(false);
      LocalStorageService.remove("MAINTENANCE_FORM");
      updatePageAction({
        type: "add",
        message: result.message,
        success: result.success,
        title: "Asset Maintenance Schedule",
      });
      openModel("alert");
    } else {
      const isFieldValid = isFormValid([], ["assets"]);
      if (isFieldValid && !form.assets.isValid) {
        updatePageAction({
          type: "add",
          message:
            "Please select atleast one Asset from table  below to continue",
          success: false,
          title: "Asset Selection is Required",
        });
        // openModel("alert");
      } else {
        updatePageAction((prev) => ({
          ...prev,
          message: "Please fill all required fields",
        }));
      }
    }
  }

  const navigate = useNavigate();
  function onSeeAllPress() {
    navigate(appRoutePath.ASSET_MAINTENANCE_HISTORY_PAGE);
  }

  function discardChanges() {
    LocalStorageService.remove("MAINTENANCE_FORM");
    setUnsavedChanges(false);
    onSeeAllPress();
  }
  function onExportBtnClick() {
    alert("Exporting Maintenance Schedule ....");
  }
  const resetPage = () => {
    updatePageAction({});
    resetForm();
    resetSelectedRowItems();
    closeModel();
  };
  const requestMessage = pageAction?.message ?? "";
  const requestTitle = pageAction?.title ?? "";
  const isRequestSuccess = pageAction?.success ?? false;

  return (
    <MainPageBase
      title="Asset Maintenance Schedule "
      pageState={"add-item"}
      cancelBtnClick={cancelAddItem}
      addItemBtnOnClick={onSaveItemClick}
    >
      <Box height={`100%`} display={"flex"} justifyContent={"center"}>
        <Stack width={"100%"}>
          <Box width={"100%"}>
            <Stack direction={"row"} gap={4}>
              <ExpandableContainer
                label="Maintenance Schedule Information"
                initOpenState={true}
              >
                <Stack gap={3} pb={3} pr={3} minHeight={350}>
                  <Stack pt={3} gap={4} direction={"row"}>
                    <DatePicker
                      handleOnchange={handleDatePicker}
                      label="Service  Date"
                      value={dayjs(form.service_date.value)}
                      error={fieldHasError("service_date")}
                      errorMessage={form.service_date.errorMessage}
                      required={true}
                    />

                    <ItemSelectInput
                      label="Schedule Status"
                      placeholder="Select Schedule Status"
                      items={ASSET_SCHEDULE_STATUS}
                      item=""
                      value={form.schedule_status.value}
                      error={fieldHasError("schedule_status")}
                      handleChange={updateInputField("schedule_status")}
                      errorMessage={form.schedule_status.errorMessage}
                      required={true}
                    />
                  </Stack>

                  <ItemInput
                    label="Service Remarks"
                    placeholder="Long text"
                    handleChange={updateInputField("service_remarks")}
                    value={form.service_remarks.value}
                    error={fieldHasError("service_remarks")}
                    errorMessage={form.service_remarks.errorMessage}
                    size="full"
                    multiline
                    required={true}
                  />
                </Stack>
              </ExpandableContainer>
            </Stack>
          </Box>

          <AssetLogsFilter<IAssetRecord[]>
            ftn={assetApi.getAssets}
            updateDataSet={populateDataSet}
            updateQuerySet={setQuerySet}
          />
          <DamsTable
            tableTitle="Assets List"
            tableSubtitle=" "
            useTableCheckbox={true}
            selectedRowItems={selectedRowItems}
            tableHeaders={columns}
            tableKeys={tkeys}
            hiddenKeys={hkeys}
            hiddenLabels={hcolumns}
            data={dataset}
            maxRowCells={6}
            onTableItemSelect={toggleRow}
            toggleSelectAllRow={toggleSelectAllRow}
            hasPagination
            paginationState={pagination}
            setActivePage={paginateToPageNumber(populateDataSet, querySet)}
            onNextPage={paginateToNextPage(querySet, populateDataSet)}
            onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
            tableTitleBadge={
              selectedRowItems.length === 0
                ? undefined
                : selectedRowItems.length + " selected"
            }
          />
        </Stack>
      </Box>

      {/* Modals  */}
      {/* succesfully saved user  */}
      <ActionModal
        show={models.showAlertModel}
        title={requestTitle}
        leftIcon={!isRequestSuccess ? DamsAppAsset.icons.warning : undefined}
        subtitle={requestMessage}
        onLeftBtnPress={isRequestSuccess ? onSeeAllPress : resetPage}
        leftBtnTxt={isRequestSuccess ? "See All" : "close"}
        onRightBtnPress={isRequestSuccess ? resetPage : closeModel}
        rightBtnTxt={isRequestSuccess ? "Schedule New" : "Retry"}
        onClosePress={resetPage}
      />

      {/* confirm user save */}
      <ConfirmActionModal
        show={models.showAddModel}
        title={requestTitle}
        subtitle={requestMessage}
        onRightBtnPress={confirmUserSave}
        rightBtnTxt={"Confirm"}
        onLeftBtnPress={closeModel}
        onRightBtnLoading={isProcessing}
        leftBtnTxt={"Cancel"}
        onClosePress={closeModel}
      />

      {/* confirm unsaved changes */}
      <ConfirmActionModal
        show={unsavedChanges}
        title={"Unsaved Changes"}
        subtitle={`Do you want to save changes or discard them?`}
        onRightBtnPress={() => saveChanges()}
        rightBtnTxt={"Save Changes"}
        onLeftBtnPress={() => discardChanges()}
        leftBtnTxt={"Discard"}
        onClosePress={() => setUnsavedChanges(false)}
      />
    </MainPageBase>
  );
}

export { AssetMaintenanceSchedule };

const tkeys = [
  "asset_category_name",
  "serial_number",
  "brand",
  "embossment_number",
  "assigned",
  "date_received",
];

const columns = [
  "Asset Category",
  "Serial Number",
  "Brand (Model)",
  "Embossment Number",
  "Assignment Status",
  "Date Received",
];
const hkeys: (keyof IAssetRecord)[] = [
  "batch_number",
  "current_location",
  "current_location_domain",
  "description",
  "warranty_in_months",
  "asset_meta_raw",
];

const hcolumns = [
  "Batch Number",
  "Current Location",
  "current location domain",
  "Description",
  "Warranty (months)",
  "Additional Information",
];
